import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';

/**
 * SSE hook — connects to the server event stream.
 * When another recruiter changes something, auto-invalidates the relevant TanStack Query cache.
 * This means all connected recruiters see updates in near-real-time without polling.
 */
export function useRealtimeSync(enabled = true) {
  const qc = useQueryClient();
  const esRef = useRef(null);
  const retryRef = useRef(0);

  useEffect(() => {
    if (!enabled) return;

    const connect = () => {
      // Cookies are sent automatically for same-origin EventSource
      const url = `/api/sse/events`;
      const es = new EventSource(url);
      esRef.current = es;

      es.onopen = () => {
        retryRef.current = 0;
      };

      es.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data);
          if (msg.type === 'connected') return;

          // Map SSE event types to TanStack Query keys to invalidate
          const invalidationMap = {
            applications: ['sourced-pending', 'pending-info-requests', ['dashboard', 'all']],
            jobs: ['jobs', ['dashboard', 'all']],
            candidates: ['candidates', ['dashboard', 'all']],
            notifications: [['dashboard', 'all']],
            dashboard: [['dashboard', 'all']],
          };

          const keys = invalidationMap[msg.type] || [];
          keys.forEach((key) => {
            qc.invalidateQueries({ queryKey: Array.isArray(key) ? key : [key] });
          });
        } catch {
          // Ignore parse errors from heartbeats
        }
      };

      es.onerror = () => {
        es.close();
        esRef.current = null;
        // Exponential backoff retry: 1s, 2s, 4s, 8s, max 30s
        const delay = Math.min(1000 * Math.pow(2, retryRef.current), 30000);
        retryRef.current++;
        setTimeout(connect, delay);
      };
    };

    connect();

    return () => {
      if (esRef.current) {
        esRef.current.close();
        esRef.current = null;
      }
    };
  }, [enabled, qc]);
}
